b-row
<template>
  <div class="w-100">
    <div class="hero__banner">
      <b-col
        cols="12"
        class="hero__bg pt-5 px-3 px-md-0 d-flex justify-content-left"
        :style="{ backgroundImage: `url('/congress/img/events/${eventId}/hero-bg.png?v=${version}')` }"
      >
        <div class="eventInfo mr-auto">
          <div>
            <img :src="`${publicPath}img/events/${eventId}/logo.png?v=${this.appVersion}`" class="navbar-logo" alt="Congress Logo" />
          </div>
          <h1>Simulation village</h1>
        </div>
        <div class="pt-5 mr-4">
          <b-button pill class="btn-danger border-0" :href="`simulation-village-host?password=${password}`">Current sessions</b-button>
        </div>
        <div class="float-right pr-5 pt-5" v-if="!displayError">
          <b-navbar-nav>
            <b-nav-form>
              <b-form-input v-model="searchQuery" @keydown.enter.prevent type="search" class="mr-sm-2 rounded-pill" placeholder="Search by ESC ID..." />
            </b-nav-form>
          </b-navbar-nav>
        </div>
      </b-col>
    </div>
    <b-container fluid class="px-lg-5" v-if="!displayError">
      <b-row>
        <b-col cols="12">
          <div id="topStickyDays" class="timeLine px-lg-3">
            <b-row class="p-2 mb-4 font-black">
              <b-col v-for="(day, index) in eventDays" :key="day.toString()" class="date__text" :class="{ selected: index === eventDaySelectedIndex }">
                <span @click="updateTab(index)" class="d-block">{{ day | formatUnixEpocDayPart }}</span>
              </b-col>
            </b-row>
          </div>
        </b-col>
      </b-row>
      <b-container fluid class="mb-3 px-0">
        <b-row no-gutters>
          <b-col v-if="isLoading" cols="12">
            <div  class="d-flex justify-content-center m-5">
              <b-spinner style="width: 2rem; height: 2rem" variant="congress" label="loading"></b-spinner>
            </div>
          </b-col>
          <template v-else>
            <b-col cols="12" md="6" lg="4" xl="3" class="card-deck pb-2 pb-md-4 px-0 mx-0" v-for="workshop in workshops" :key="workshop.id">
              <b-card :title="workshop.displayName" class="session__card">
                <template #header>
                  <div class="pb-2">
                    <div class="session__time font-bold d-block">
                      {{ workshop.date | formatUnixEpocDayPart }} • {{ workshop.startTime | formatTime }} -
                      {{ workshop.endTime | formatTime }}
                    </div>
                    <div class="session__time font-bold d-block">
                      {{ workshop.roomName }}
                    </div>
                  </div>
                </template>
                <b-card-text>
                  <b-list-group>
                    <b-list-group-item v-for="contact in workshop.contacts" :key="contact.ESCID">
                      {{ contact.name }}
                    </b-list-group-item>
                  </b-list-group>
                </b-card-text>
              </b-card>
            </b-col>
          </template>
        </b-row>
      </b-container>
    </b-container>
    <b-container v-else>
      <AccessDeniedBlock />
    </b-container>
  </div>
</template>

<script>
  import { DateHelper } from 'src/utils/date-helper';
  import { mapGetters, mapState } from 'vuex';
  import { MediaApiService } from 'src/services/media-api';
  import AccessDeniedBlock from 'src/components/Blocks/AccessDeniedBlock.vue';

  export default {
    name: 'SimulationVillage',
    components: {
      AccessDeniedBlock,
    },
    computed: {
      publicPath() {
        return process.env.BASE_URL;
      },
      ...mapGetters(['isLogged', 'currentUser']),
      ...mapState({
        eventId: (state) => state.app.eventId,
        startDate: (state) => state.app.eventStartDate,
        endDate: (state) => state.app.eventEndDate,
        location: (state) => state.app.location,
        version: (state) => state.app.version,
        userInfo: (state) => state.app.userInfo,
        slotSelection: (state) => state.app.workshopCart,
        homeCmsData: (state) => state.app.homeCmsData,
        appVersion: (state) => state.app.version,
      }),
      isPastSlot() {
        return this.serverDate && new Date(this.eventDays[this.eventDaySelectedIndex].toDateString()) < new Date(this.serverDate.toDateString());
      },
      returnUrl() {
        return window.location.origin + this.$route.fullPath;
      },
    },
    data() {
      return {
        workshops: [],
        allWorkshops: [],
        eventDays: [],
        eventDaySelectedIndex: undefined,
        serverDate: null,
        isLoading: true,
        searchQuery: '',
        password: null,
        displayError: false,
      };
    },
    watch: {
      searchQuery() {
        this.applyFilter();
      },
    },
    async mounted() {
      this.getQueryPassword();

      this.eventDays = DateHelper.getDates(DateHelper.onSiteDateTime(this.startDate), DateHelper.onSiteDateTime(this.endDate));
      try {
        const { data } = await MediaApiService.getTime();
        this.serverDate = DateHelper.onSiteDateTime(data);
        this.eventDaySelectedIndex = Math.max(
          0,
          this.eventDays.findIndex((x) => x.toDateString() === this.serverDate.toDateString())
        );

        await this.fetchWorkshopData(this.eventDays[this.eventDaySelectedIndex]);
      } catch (err) {
        console.error(err);
        this.eventDaySelectedIndex = 0;
      }
    },
    methods: {
      getQueryPassword() {
        this.password = this.$route.query.password;
      },

      async fetchWorkshopData(date) {
        this.isLoading = true;

        MediaApiService.getAllWorkshopsByDate(this.eventId, DateHelper.formatISODate(date), this.password).then(async ({ data }) => {
          this.allWorkshops = data.data;
          this.isLoading = false;
          this.displayError = !data.success;
          this.applyFilter();
        });
      },

      searchWorkshops() {
        this.fetchWorkshopData(this.eventDays[this.eventDaySelectedIndex]);
      },

      applyFilter() {
        if (!this.searchQuery) {
          this.workshops = this.allWorkshops;
          return;
        }
        this.workshops = this.allWorkshops.filter((workshop) => workshop.contacts.some((contact) => contact.escid && contact.escid.includes(this.searchQuery)));
      },

      async updateTab(index) {
        if (this.eventDaySelectedIndex !== index) {
          this.eventDaySelectedIndex = index;
          await this.fetchWorkshopData(this.eventDays[index]);
        }
      },

      getTimeStringSeconds(time) {
        const timeArray = time.split(':');
        return Number(timeArray[0]) * 3600 + Number(timeArray[1]) * 60 + Number(timeArray[2]);
      },

      isRangeOverlap(xStart, xEnd, yStart, yEnd) {
        return xStart < yEnd && yStart < xEnd;
      },

      redirectToCheckout() {
        const slotIds = this.slotSelection.map((x) => x.id);
        const url = `${process.env.VUE_APP_MY_PROFILE_URL}order-workshop?returnUrl=${this.returnUrl}&slotIds=${slotIds.join(',')}`;
        window.location = url;
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import '~@/styles/theme';
  @import '~@/styles/theme';
  @import 'node_modules/bootstrap/scss/_functions';
  @import 'node_modules/bootstrap/scss/_variables';
  @import 'node_modules/bootstrap/scss/mixins/_breakpoints';

  .signIn__card {
    background: var(--congress);
    border-radius: 2rem;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
    color: #fff;
    overflow: hidden;
  }

  .rounded-line {
    border-radius: 9999px;
    border: 1px solid var(--congress-light);
    transform: translate(50%, -50%);
  }

  .session__card-disabled {
    background-color: #fff;
    opacity: 0.4;
  }

  .session__card .btn-light:hover,
  .session__card .btn-light:active,
  .session__card .btn-light:focus {
    background-color: var(--congress-lighter) !important;
    color: var(--congress);
  }

  .btn-congress:hover,
  .btn-congress:active,
  .btn-congress:focus {
    background-color: var(--congress) !important;
    color: #fff;
  }

  .btn-light.disabled,
  .btn-light.disabled:hover {
    background-color: #f1f1f1 !important;
    color: #212529;
  }

  .session__card-disabled .btn-light.disabled:hover {
    background-color: #f1f1f1 !important;
    color: #212529;
  }

  ::v-deep .cart__card {
    background: #fff;
    border-radius: 2rem;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
    color: #000;
    .cart__element {
      .dotted__separator {
        border-bottom: 2px dotted #e5e5e5;
        :last-of-type {
          border-bottom: 0 !important;
        }
      }
    }
  }

  .grey__text {
    color: #5f6368;
  }

  .list-group {
    background: #f3f6f7;
    border-radius: 20px;
    .list-group-item {
      font-size: 0.9rem;
      border: 0;
      border-bottom: 1px dotted rgba(0, 0, 0, 0.125);
      background: transparent;
    }
  }
  .list-group-item:last-child {
    border-bottom: 0;
  }

  .form-inline .form-control {
    min-width: 300px;
  }
</style>
